/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { QUERY_PERSIST_CACHE } from '@airasia-rn/ui';
import { useSetting } from '@airasia-rn/account/hooks';
import type { HotelRecentSearch, RoomConfig, Suggestions } from '../types';
import {
  MAX_RECENT_SEARCHES,
  HOTEL,
  TRAVEL_TYPE,
  CACHE_MAX_AGE,
} from '../configs/constants';
import { SMARTFILL_LAST_RECENT_SEARCHES_CT } from '../configs/settingsConstant';
import { isQueryKeyEqual } from './queryKeyUtils';
import { flattenHotelRecentSearchesForHistory } from '../utils/hotelSearchHistory';

export { flattenHotelRecentSearchesForHistory };

export const RECENT_SEARCHES_QUERY_KEY = [
  QUERY_PERSIST_CACHE,
  'hotels',
  'recentSearches',
] as const;

const EMPTY_RECENT_SEARCHES: HotelRecentSearch[] = [];

const isRecentSearchesQuery = (queryKey: readonly unknown[]) =>
  isQueryKeyEqual(queryKey, RECENT_SEARCHES_QUERY_KEY);

/**
 * Returns the persist query for hotel recent searches (`{ data }`).
 */
export function useHotelRecentSearches() {
  const queryClient = useQueryClient();

  useMemo(() => {
    queryClient.setQueryDefaults(RECENT_SEARCHES_QUERY_KEY, {
      staleTime: CACHE_MAX_AGE,
      gcTime: CACHE_MAX_AGE,
    });
  }, [queryClient]);

  return useQuery<HotelRecentSearch[]>({
    queryKey: RECENT_SEARCHES_QUERY_KEY,
    queryFn: () => EMPTY_RECENT_SEARCHES,
    staleTime: CACHE_MAX_AGE,
    gcTime: CACHE_MAX_AGE,
    enabled: false,
  });
}

export function useRecentSearches() {
  const queryClient = useQueryClient();

  const { value: recentSearchCountSetting } = useSetting(
    SMARTFILL_LAST_RECENT_SEARCHES_CT,
  );
  const recentSearchCount = recentSearchCountSetting
    ? parseInt(recentSearchCountSetting, 10)
    : MAX_RECENT_SEARCHES;

  const { data: recentSearches = EMPTY_RECENT_SEARCHES } =
    useHotelRecentSearches();

  const recentSearchesRef = useRef(recentSearches);
  recentSearchesRef.current = recentSearches;

  const addRecentSearch = useCallback(
    (
      to: Suggestions,
      checkin: string = '',
      checkout: string = '',
      roomConfigs: RoomConfig[] = [{ id: '1', adults: 2, children: [] }],
    ) => {
      const existingSearches =
        queryClient.getQueryData<HotelRecentSearch[]>(
          RECENT_SEARCHES_QUERY_KEY,
        ) ?? recentSearchesRef.current;

      const newSearch: HotelRecentSearch = {
        destination: {
          id: to.id,
          aa_id: to.aa_id,
          name: to.name,
          full_name: to.full_name,
          coordinates: to.coordinates,
          type: to.type,
          country_code: to.country_code,
          city_id: to.city_id,
          mainDisplayName: to.name,
          subDisplayName: to.full_name,
          iconDisplayName: to.type,
          iconName: to.type === TRAVEL_TYPE ? 'FlightIcon' : 'HotelIcon',
          isClickedDestinationItem: true,
        },
        date: {
          checkin,
          checkout,
        },
        rooms: roomConfigs.map((room) => ({
          adult: room.adults,
          child: room.children.length,
          infant: room.infants ?? 0,
        })),
        roomConfigs,
      };

      const filtered = existingSearches.filter(
        (search) => search.destination.id !== to.id,
      );

      const updated = [newSearch, ...filtered].slice(
        0,
        Math.max(1, recentSearchCount),
      );

      // Update query cache to trigger re-render in all components.
      // TanStack Query with QUERY_PERSIST_CACHE will automatically persist to storage.
      queryClient.setQueryData(RECENT_SEARCHES_QUERY_KEY, updated);
    },
    [queryClient, recentSearchCount],
  );

  const clearRecentSearches = useCallback(() => {
    queryClient.setQueryData(RECENT_SEARCHES_QUERY_KEY, []);
  }, [queryClient]);

  const getRecentDestinations = useCallback(
    (max: number) => {
      return recentSearches.slice(0, max).map((search) => ({
        station: search.destination,
        isAllAirports: search.destination.type === HOTEL,
      }));
    },
    [recentSearches],
  );

  return {
    recentSearches,
    isLoading: false,
    addRecentSearch,
    clearRecentSearches,
    getRecentDestinations,
  };
}
